import React, { useState, useEffect } from "react";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { FaTrash } from "react-icons/fa";
import { IoIosAddCircle } from "react-icons/io";
import { MdRemoveCircle } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { addProductToCart, updateCartItemQuantity, removeProductFromCart } from "../redux/cartSlice";
import { addProductToWishlist, removeProductFromWishlist } from "../redux/WishlistSlice";
import { getFallbackImage } from "../utils/imageLoader";
import { getFullImageUrl } from "../api";
import ProductModal from "./ProductModal";
import styles from "./Card.module.css";

const Card = React.forwardRef(({ post, isWishlistPage = false, onClick }, ref) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const dispatch = useDispatch();

  const wishlistItems = useSelector(state => state.wishlist.items);
  const cartItems = useSelector(state => state.cart.items);

  const isInWishlist = post ? wishlistItems.some(item => item.id === post._id) : false;
  const currentItem = post ? cartItems.find(item => item.id === post._id) : null;
  const quantity = currentItem ? currentItem.quantity : 0;

  const images = post?.images?.length > 0 ? post.images.map(getFullImageUrl) : [];
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    if (hasMultipleImages) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % images.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [hasMultipleImages, images.length]);

  const price = post?.variants?.[0]?.price || 0;

  const handleAddToCart = () => {
    if (!post?.variants?.length) return;
    const firstVariant = post.variants[0];
    const safeImage = images[0] || getFallbackImage();
    dispatch(addProductToCart({
      ...post,
      id: post._id,
      price: firstVariant.price,
      image: safeImage,
      title: post.name,
      variantId: firstVariant._id,
      selectedColor: firstVariant.color,
      selectedSize: firstVariant.size
    }, 1));
  };

  const handleIncrement = () => {
    if (currentItem) dispatch(updateCartItemQuantity(currentItem, quantity + 1));
  };

  const handleDecrement = () => {
    if (currentItem) {
      if (quantity === 1) {
        dispatch(removeProductFromCart(currentItem));
      } else {
        dispatch(updateCartItemQuantity(currentItem, quantity - 1));
      }
    }
  };

  const toggleWishlist = () => {
    const safeImage = images[0] || getFallbackImage();
    if (isInWishlist) {
      const wishlistItem = wishlistItems.find(item => item.id === post._id);
      if (wishlistItem) dispatch(removeProductFromWishlist(wishlistItem));
    } else {
      dispatch(addProductToWishlist({
        ...post,
        id: post._id,
        price,
        image: safeImage,
        title: post.name
      }));
    }
  };

  const handleRemoveFromWishlist = () => {
    const wishlistItem = wishlistItems.find(item => item.id === post._id);
    if (wishlistItem) dispatch(removeProductFromWishlist(wishlistItem));
  };

  if (!post) return null;

  return (
    <>
      <div className={styles.card} data-aos="fade-up" onClick={onClick} ref={ref}>
        {isWishlistPage && (
          <button className={styles.removeWishlistButton}
            onClick={(e) => { e.stopPropagation(); handleRemoveFromWishlist(); }}>
            <FaTrash />
          </button>
        )}

        {/* <div className={styles.categoryTag}>
          {post.category?.name || "Category"}
        </div> */}

        

        <div className={styles.imageContainer}>
          {hasMultipleImages ? (
            <div style={{ display: "flex", transition: "transform 0.5s ease", transform: `translateX(-${currentImageIndex * 100}%)` }}>
              {images.map((imgUrl, idx) => (
                <img key={idx} src={imgUrl} alt={`${post.name}-${idx}`}
                  className={styles.image}
                  onClick={(e) => { e.stopPropagation(); setModalOpen(true); }} />
              ))}
            </div>
          ) : (
            <img src={images[0] || getFallbackImage()} alt={post.name}
              className={styles.image}
              onClick={(e) => { e.stopPropagation(); setModalOpen(true); }} />
          )}
        </div>
        <p className={styles.title} onClick={() => setModalOpen(true)} style={{ cursor: "pointer" }}>{post.name}</p>

        <div className={styles.priceWishlistContainer}>
          <p className={styles.price}>₹{price.toFixed(2)}</p>

          {!isWishlistPage && (
            <>
              {quantity === 0 ? (
                <button className={styles.button}
                  onClick={(e) => { e.stopPropagation(); handleAddToCart(); }}>
                  Add to Cart
                </button>
              ) : (
                <div className={styles.quantityContainer}>
                  <button className={styles.quantityButton}
                    onClick={(e) => { e.stopPropagation(); handleIncrement(); }}>
                    <IoIosAddCircle />
                  </button>
                  <span className={styles.quantity}>{quantity}</span>
                  <button className={styles.quantityButton}
                    onClick={(e) => { e.stopPropagation(); handleDecrement(); }}>
                    <MdRemoveCircle />
                  </button>
                </div>
              )}

              <button className={styles.wishlistButton}
                onClick={(e) => { e.stopPropagation(); toggleWishlist(); }}>
                {isInWishlist ? <AiFillHeart /> : <AiOutlineHeart />}
              </button>
            </>
          )}
        </div>
      </div>
      <ProductModal post={post} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
});

export default Card;
